<template>
  <div class="date-picker" @click.stop>
    <button class="date-trigger" type="button" @click="toggle" :aria-label="$t('选择日期')">
      <span :class="{ placeholder: !value }">{{ value || $t('选择日期') }}</span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="17" rx="2"></rect>
        <path d="M16 2v4M8 2v4M3 10h18"></path>
      </svg>
    </button>

    <div v-if="open" ref="popover" class="date-popover" :style="popoverStyle">
      <div class="date-toolbar">
        <button class="date-nav" type="button" @click="changeMonth(-1)" :aria-label="$t('上个月')">‹</button>
        <div class="date-selectors">
          <select v-model.number="viewYear" :aria-label="$t('年份')">
            <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
          </select>
          <select v-model.number="viewMonth" :aria-label="$t('月份')">
            <option v-for="month in 12" :key="month - 1" :value="month - 1">{{ monthLabels[month - 1] }}</option>
          </select>
        </div>
        <button class="date-nav" type="button" @click="changeMonth(1)" :aria-label="$t('下个月')">›</button>
      </div>

      <div class="date-weekdays">
        <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
      </div>
      <div class="date-grid">
        <span v-for="(day, index) in calendarDays" :key="day ? day.value : `blank-${index}`" class="date-cell">
          <button
            v-if="day"
            type="button"
            :class="{ selected: day.value === value, today: day.value === todayValue }"
            @click="selectDay(day.value)"
          >{{ day.day }}</button>
        </span>
      </div>

      <div class="date-footer">
        <button type="button" @click="selectDay(todayValue)">{{ $t('今天') }}</button>
        <button type="button" v-if="value" @click="clear">{{ $t('清除') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DatePicker',
  props: {
    value: { type: String, default: '' }
  },
  data() {
    const base = this.parseDate(this.value) || new Date()
    return {
      open: false,
      viewYear: base.getFullYear(),
      viewMonth: base.getMonth(),
      popoverStyle: { top: '12px', left: '12px', width: 'min(286px, calc(100vw - 24px))' }
    }
  },
  computed: {
    locale() { return this.$store.state.locale === 'en-US' ? 'en-US' : 'zh-CN' },
    yearOptions() {
      const selectedYear = this.parseDate(this.value)?.getFullYear() || new Date().getFullYear()
      const currentYear = new Date().getFullYear()
      const start = Math.min(currentYear - 10, selectedYear - 2)
      const end = Math.max(currentYear + 10, selectedYear + 2)
      return Array.from({ length: end - start + 1 }, (_, index) => start + index)
    },
    monthLabels() {
      return Array.from({ length: 12 }, (_, month) => new Intl.DateTimeFormat(this.locale, { month: 'long' }).format(new Date(2020, month, 1)))
    },
    weekdayLabels() {
      const labels = this.locale === 'en-US' ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : ['日', '一', '二', '三', '四', '五', '六']
      return labels
    },
    todayValue() { return this.formatDate(new Date()) },
    calendarDays() {
      const firstDay = new Date(this.viewYear, this.viewMonth, 1).getDay()
      const totalDays = new Date(this.viewYear, this.viewMonth + 1, 0).getDate()
      const days = Array.from({ length: firstDay }, () => null)
      for (let day = 1; day <= totalDays; day++) {
        days.push({ day, value: this.formatDate(new Date(this.viewYear, this.viewMonth, day)) })
      }
      return days
    }
  },
  watch: {
    value(value) {
      const date = this.parseDate(value)
      if (date) {
        this.viewYear = date.getFullYear()
        this.viewMonth = date.getMonth()
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.closeOnOutside)
    window.addEventListener('resize', this.reposition)
    document.addEventListener('scroll', this.reposition, true)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeOnOutside)
    window.removeEventListener('resize', this.reposition)
    document.removeEventListener('scroll', this.reposition, true)
  },
  methods: {
    parseDate(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null
      const [year, month, day] = String(value).split('-').map(Number)
      const date = new Date(year, month - 1, day)
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null
    },
    formatDate(date) {
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    },
    toggle() {
      this.open = !this.open
      if (this.open) this.$nextTick(this.reposition)
    },
    reposition() {
      if (!this.open || !this.$refs.popover) return

      const trigger = this.$el.querySelector('.date-trigger').getBoundingClientRect()
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight
      const horizontalPadding = 12
      const gap = 8
      const width = Math.min(286, Math.max(220, viewportWidth - horizontalPadding * 2))
      const popover = this.$refs.popover.getBoundingClientRect()
      const height = popover.height || 360
      const left = Math.max(
        horizontalPadding,
        Math.min(trigger.left, viewportWidth - width - horizontalPadding)
      )
      const belowTop = trigger.bottom + gap
      const aboveTop = trigger.top - height - gap
      const top = belowTop + height <= viewportHeight - horizontalPadding
        ? belowTop
        : aboveTop >= horizontalPadding
          ? aboveTop
          : Math.max(horizontalPadding, viewportHeight - height - horizontalPadding)

      this.popoverStyle = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${Math.round(width)}px`
      }
    },
    closeOnOutside(event) {
      if (this.open && !this.$el.contains(event.target)) this.open = false
    },
    changeMonth(offset) {
      const date = new Date(this.viewYear, this.viewMonth + offset, 1)
      this.viewYear = date.getFullYear()
      this.viewMonth = date.getMonth()
    },
    selectDay(value) {
      this.$emit('input', value)
      this.$emit('change', value)
      this.open = false
    },
    clear() {
      this.$emit('input', '')
      this.$emit('change', '')
      this.open = false
    }
  }
}
</script>

<style scoped>
.date-picker { position: relative; min-width: 0; }
.date-trigger {
  width: 100%; height: 38px; padding: 0 11px; display: flex; align-items: center;
  justify-content: space-between; gap: 8px; border: 1px solid #1b4a2d; border-radius: 7px;
  background: #07100a; color: var(--tech-text); font-size: 12px; cursor: pointer;
}
.date-trigger .placeholder { color: var(--tech-muted); }
.date-trigger:hover, .date-trigger:focus { border-color: var(--tech-mint); outline: none; }
.date-popover {
  position: fixed; z-index: 2100; box-sizing: border-box; max-height: calc(100vh - 24px); overflow-y: auto;
  padding: 12px; border: 1px solid #356b47; border-radius: 8px; background: #0b1710;
  box-shadow: 0 18px 38px rgba(0, 0, 0, .5);
}
.date-toolbar, .date-footer { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.date-selectors { display: flex; flex: 1; gap: 6px; }
.date-selectors select {
  min-width: 0; height: 32px; padding: 0 5px; flex: 1; border: 1px solid #356b47;
  border-radius: 5px; background: #102319; color: var(--tech-text); font-size: 12px; outline: none;
}
.date-selectors select:focus { border-color: var(--tech-mint); }
.date-nav {
  width: 30px; height: 30px; flex: 0 0 30px; border: 1px solid #356b47; border-radius: 5px;
  background: #173a24; color: var(--tech-mint); font-size: 22px; line-height: 20px; cursor: pointer;
}
.date-weekdays, .date-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; }
.date-weekdays { margin: 12px 0 5px; color: var(--tech-muted); font-size: 10px; text-align: center; }
.date-cell { height: 29px; display: flex; align-items: center; justify-content: center; }
.date-cell button {
  width: 28px; height: 28px; border: 1px solid transparent; border-radius: 5px;
  background: transparent; color: var(--tech-text); font-size: 12px; cursor: pointer;
}
.date-cell button:hover { background: #173a24; border-color: #356b47; color: var(--tech-mint); }
.date-cell button.today { border-color: #356b47; color: var(--tech-mint); }
.date-cell button.selected { background: var(--tech-mint); border-color: var(--tech-mint); color: var(--tech-black); font-weight: 900; }
.date-footer { margin-top: 9px; padding-top: 9px; border-top: 1px solid #356b47; }
.date-footer button { border: 0; background: transparent; color: var(--tech-mint); font-size: 11px; cursor: pointer; }
</style>
